import React, { FC, createContext } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import {
  INSIGHTS_DASHBOARD_VIEW_QUERY,
  InsightsDashboardViewResult,
  CompanyAllTimeRangeResult,
  COMPANY_ALL_TIME_RANGE_QUERY
} from 'graphql/queries/insights/insights.query';
import { EVENTS_LIST_ROUTE } from 'constants/routes';
import { InsightsDashboardView } from 'types/insights-dashboard.types';
import { CompanyAllTimeRange } from 'types/company.types';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';

export interface DashboardViewContextState {
  view: InsightsDashboardView;
  allTimeRange?: CompanyAllTimeRange;
}

const DashboardViewContext = createContext<DashboardViewContextState>(
  {} as DashboardViewContextState
);

export const DashboardViewProvider: FC = () => {
  const { data, loading } = useQuery<InsightsDashboardViewResult>(INSIGHTS_DASHBOARD_VIEW_QUERY, {
    fetchPolicy: 'network-only'
  });

  const { data: allTimeRangeData, loading: allTimeRangeLoading } =
    useQuery<CompanyAllTimeRangeResult>(COMPANY_ALL_TIME_RANGE_QUERY, {
      fetchPolicy: 'cache-first'
    });

  const ctxValue: DashboardViewContextState = React.useMemo(
    () => ({
      view: data?.insightsDashboardView || ({} as InsightsDashboardView),
      allTimeRange: allTimeRangeData?.companyAllTimeRange
    }),
    [data, allTimeRangeData]
  );

  if (loading || allTimeRangeLoading) {
    return <LoadingSpinner fullscreen />;
  }

  if (!data) {
    console.error('no dashboard view found.');
    return <Navigate to={EVENTS_LIST_ROUTE} />;
  }

  return (
    <DashboardViewContext.Provider value={ctxValue}>
      <Outlet />
    </DashboardViewContext.Provider>
  );
};

export default DashboardViewContext;
